import type { AvailableReward } from 'api/recoveryAi.types'
import { buildTranslate } from 'locales'
import { currencyFromCents } from 'utilities/formatters'

// Shared by RewardMultiSelect (recovery_ai.reward_picker namespace, via
// RewardMultiSelect/labels.ts) and the make-good ladder
// (recovery_ai.settings.approved_rewards.ladder namespace). Those two
// components live in different i18n namespaces, so the "%{amount} value"
// copy has to be resolved from ONE shared block
// (recovery_ai.reward_value — see src/locales/recoveryAi.ts) or the wording
// can drift between them, exactly the "one source" rationale already applied
// to recovery_ai.reward_picker and brand_voice.
const t = buildTranslate('recovery_ai.reward_value')

/**
 * Resolves the GUEST-PERCEIVED value of a reward in integer cents. This is
 * the ONLY amount that may render on a reward-approval surface (display
 * contract: guest value only — COGS/`cost_cents` never renders on its own).
 *
 * `value_cents` is nexus `max_value_cents` (`perceived_value * 100`). It is
 * unset (`null`) for `Manual::Amount` dollar-credit templates — 8 of the 54
 * approved templates in production — because those templates have no
 * separate "perceived value" concept: `Manual::Amount#calculate_redeem_cost`
 * returns the FULL `discount` as cost, so a $5 credit costs $5 AND is worth
 * $5 to the guest. Cost IS value for that one template type, which is why the
 * `cost_cents` fallback below is correct.
 *
 * The fallback is NOT generally correct for item templates (their COGS runs
 * 10-30% of guest value) — but item templates always have `perceived_value`
 * set server-side, so they never actually hit the fallback in practice. Do
 * not "fix" this by tightening the FE types (e.g. making `value_cents`
 * required) — the nullability on the wire is real and this fallback is the
 * intended handling of it, not a gap to close.
 */
export const resolveRewardValueCents = (reward: AvailableReward): number =>
  reward.value_cents ?? reward.cost_cents

/**
 * Formats a guest-value cents amount as "$X.XX value". Reuses
 * `currencyFromCents` (utilities/formatters) rather than hand-rolling a
 * formatter — do not reintroduce `.toFixed(0)` here, which is the bug this
 * module exists to fix: it rounded sub-dollar guest values (e.g. $0.79) down
 * to a bare "$0".
 */
export const formatRewardValue = (cents: number): string =>
  t('suffix', { amount: currencyFromCents(cents) })
